import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Table } from "antd";
import {
  Groups as GroupsIcon,
  Warning as WarningIcon,
  AccountBalanceWallet as WalletIcon,
  FilterListOutlined,
  Payments as PaymentsIcon,
  ReportProblem as ReportProblemIcon,
  Block as BlockIcon,
  Devices as DevicesIcon,
} from "@mui/icons-material";
import GenericPieChart from "../../charts/GenericPieChart";
import GenericLineChart from "../../charts/GenericLineChart";
import Loader from "../../common/Loader";
import useGet from "../../../hooks/common/useGet";
import {
  API_FRAUD_DASHBOARD_EVENTS,
  API_FRAUD_EVENTS,
  API_FRAUD_RISK_TRENDS,
} from "../../../utils/apis";
import dayjs from "dayjs";
import {
  roundNumber,
  getDatesInRange,
  formatCurrency,
} from "../../../utils/helpers";
import { blueColor } from "../../styled/Theme";

const FraudDashboard = () => {
  const [chartStartDate, setChartStartDate] = useState(
    dayjs().startOf("month").format("YYYY-MM-DD"),
  );

  const [chartEndDate, setChartEndDate] = useState(
    dayjs().endOf("month").format("YYYY-MM-DD"),
  );

  const [dailyData, setDailyData] = useState<
    { date: string; suspicious: number }[]
  >([]);

  const [filter, setFilter] = useState(false);

  const { loadData, loading, data } = useGet();
  const {
    loadData: loadEvents,
    loading: eventLoading,
    data: eventsData,
  } = useGet();
  const {
    loadData: loadChartEvents,
    loading: eventChartsLoading,
    data: eventsChartData,
  } = useGet();

  // Fetch dashboard metrics
  useEffect(() => {
    fetchChartData();
  }, []);

  // Fetch chart data based on start/end date
  const fetchChartData = (start = chartStartDate, end = chartEndDate) => {
    loadData({
      api: API_FRAUD_DASHBOARD_EVENTS,
      params: {
        start_date: start,
        end_date: end,
      },
    });
    loadEvents({
      api: API_FRAUD_EVENTS,
      params: { start_date: start, end_date: end },
    });
    loadChartEvents({
      api: API_FRAUD_RISK_TRENDS,
      params: {
        start_date: start,
        end_date: end,
      },
    });
  };

  useEffect(() => {
    if (!eventsChartData) return;

    const isToday =
      chartStartDate === dayjs().format("YYYY-MM-DD") &&
      chartEndDate === dayjs().format("YYYY-MM-DD");

    let mapped: { date: string; suspicious: number }[] = [];

    if (isToday) {
      // Group by hour
      mapped = Array.from({ length: 24 }, (_, i) => ({
        date: i.toString().padStart(2, "0") + ":00",
        suspicious: 0,
      }));

      eventsChartData.forEach((evt: any) => {
        const hour = dayjs(evt.created_at).hour();
        mapped[hour].suspicious += 1;
      });
    } else {
      // Group by day
      const dailyMap = eventsChartData.reduce(
        (acc: any, evt: any) => {
          const date = dayjs(evt.created_at).format("YYYY-MM-DD");
          const existing = acc.find((d: any) => d.date === date);
          if (existing) existing.suspicious += 1;
          else acc.push({ date, suspicious: 1 });
          return acc;
        },
        [] as { date: string; suspicious: number }[],
      );

      // Fill missing dates
      const allDates = getDatesInRange(chartStartDate, chartEndDate);
      mapped = allDates.map((date) => {
        const found = dailyMap.find((d) => d.date === date);
        return { date, suspicious: found ? found.suspicious : 0 };
      });
    }

    setDailyData(mapped);
  }, [eventsChartData, chartStartDate, chartEndDate]);

  // Then update xLabels
  const xLabels = dailyData.map((d) => d.date);

  const barChartData = [
    {
      label: "Fraud Risks Trends",
      data: dailyData.map((d) => d.suspicious),
      color: "#F97316",
    },
  ];

  // Pie Chart Data
  const pieChartData = data?.risk_breakdown?.map((r: any) => ({
    label: r.risk_level,
    value: r.count,
    color:
      r.risk_level === "LOW"
        ? "#10B981"
        : r.risk_level === "MEDIUM"
          ? "#FBBF24"
          : "#F87171",
  }));

  const eventTypePieData = eventsData?.results?.reduce((acc: any, evt: any) => {
    const type = evt.event_type || "UNKNOWN";
    const existing = acc.find((a: any) => a.label === type);
    if (existing) {
      existing.value += 1;
    } else {
      acc.push({ label: type, value: 1 });
    }
    return acc;
  }, []);

  const eventTypeColors = [
    blueColor,
    "#F97316",
    "#10B981",
    "#FBBF24",
    "#7C3AED",
  ];
  const eventTypePieChart = eventTypePieData?.map((e: any, i: number) => ({
    ...e,
    color: eventTypeColors[i % eventTypeColors.length],
  }));

  // Dashboard cards
  const userCardsData = [
    {
      label: "Total Events",
      value: data?.total_events || 0,
      icon: GroupsIcon,
      bgColor: "#E8F0FF",
      color: blueColor,
    },
    {
      label: "Unreviewed High-Risk",
      value: data?.unreviewed_high_risk || 0,
      icon: WarningIcon,
      bgColor: "#FFEAD5",
      color: "#D97706",
    },
    {
      label: "Blocked Events",
      value: data?.blocked_total || 0,
      icon: WalletIcon,
      bgColor: "#F0E9FF",
      color: "#7C3AED",
    },
  ];

  const metricsData = [
    {
      label: "Transaction Value",
      value: formatCurrency(data?.transaction_value_total),
      icon: PaymentsIcon,
      bgColor: "#E8F0FF",
      color: blueColor,
    },
    {
      label: "Failed Login",
      value: data?.failed_logins_total,
      icon: ReportProblemIcon,
      bgColor: "#FFF4E5",
      color: "#D97706",
    },
    {
      label: "Blocked Events",
      value: data?.blocked_total,
      icon: BlockIcon,
      bgColor: "#FEE2E2",
      color: "#DC2626",
    },
    {
      label: "New Devices",
      value: data?.new_devices_30d,
      icon: DevicesIcon,
      bgColor: "#F3E8FF",
      color: "#7C3AED",
    },
  ];

  const topUsersColumns = [
    { title: "User", dataIndex: "username", key: "user" },
    { title: "Event Count", dataIndex: "event_count", key: "event_count" },
    {
      title: "Avg Score",
      dataIndex: "avg_score",
      key: "avg_score",
      render: (_: any, record: any) => `${roundNumber(record.avg_score)}%`,
    },
  ];

  return (
    <Wrapper>
      <div style={{ display: "flex", justifyContent: "end" }}>
        <Button
          className="font12"
          type="primary"
          icon={<FilterListOutlined />}
          style={{
            borderRadius: 20,
            width: "fit",
            backgroundColor: blueColor,
          }}
          onClick={() => setFilter(!filter)}
        >
          Filter Data
        </Button>
      </div>
      {filter && (
        <FilterWrapper>
          <div style={{ display: "flex", gap: "0px", flexDirection: "column" }}>
            <label style={{ fontSize: 12 }}>Start Date</label>
            <input
              type="date"
              value={chartStartDate}
              onChange={(e) => setChartStartDate(e.target.value)}
              style={{
                padding: "6px",
                borderRadius: 6,
                border: "1px solid #ccc",
                fontSize: 12,
              }}
            />
          </div>
          <div style={{ display: "flex", gap: "0px", flexDirection: "column" }}>
            <label style={{ fontSize: 12 }}>End Date</label>
            <input
              type="date"
              value={chartEndDate}
              onChange={(e) => setChartEndDate(e.target.value)}
              style={{
                padding: "6px",
                borderRadius: 6,
                border: "1px solid #ccc",
                fontSize: 12,
              }}
            />
          </div>
          <button
            onClick={() => fetchChartData(chartStartDate, chartEndDate)}
            style={{
              padding: "8px 16px",
              background: "#2563eb",
              color: "white",
              borderRadius: 6,
              border: "none",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: 12,
            }}
          >
            Apply
          </button>
        </FilterWrapper>
      )}
      <CardsGrid>
        {userCardsData.map((tile, i) => (
          <DashboardCard key={i} backgroundColor={tile.bgColor}>
            <CardIconWrap style={{ background: tile.bgColor }}>
              <tile.icon style={{ fontSize: 70, color: tile.color }} />
            </CardIconWrap>
            <div>
              <CardLabel>{tile.label}</CardLabel>
              <CardValue>{tile.value}</CardValue>
            </div>
          </DashboardCard>
        ))}
      </CardsGrid>

      <ChartsWrapper>
        <ChartBox>
          <ChartTitle>Fraud Risk Trends</ChartTitle>

          <GenericLineChart
            data={barChartData}
            xLabels={xLabels}
            yLabel="Fraud Risks Trends"
            loading={eventChartsLoading}
          />
        </ChartBox>

        <ChartBox>
          <ChartTitle>Fraud Score Distribution</ChartTitle>
          <div style={{ display: "flex", marginTop: "20px", flexWrap: "wrap" }}>
            {loading ? (
              <Loader />
            ) : (
              <PieWrapper>
                <GenericPieChart title="Risk Levels" data={pieChartData} />
              </PieWrapper>
            )}
            {eventLoading ? (
              <Loader />
            ) : (
              <PieWrapper>
                <GenericPieChart title="Event Type" data={eventTypePieChart} />
              </PieWrapper>
            )}
          </div>
        </ChartBox>
      </ChartsWrapper>

      <TablesWrapper>
        <TableBox>
          <ChartTitle>High-Risks Users</ChartTitle>
          <Table
            dataSource={data?.top_users_30d || []}
            columns={topUsersColumns}
            rowKey="user__username"
            scroll={{ x: "max-content" }}
          />
        </TableBox>

        <TableBox>
          <ChartTitle>Key Metrics</ChartTitle>
          {loading ? (
            <Loader />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                justifyContent: "center",
                alignItems: "center",
                gap: "20px",
                flexWrap: "wrap",
                marginTop: "20px",
              }}
            >
              {metricsData.map((tile, i) => (
                <DashboardCard key={i} backgroundColor={tile.bgColor}>
                  <CardIconWrap style={{ background: tile.bgColor }}>
                    <tile.icon style={{ fontSize: 70, color: tile.color }} />
                  </CardIconWrap>
                  <div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        marginBottom: "10px",
                      }}
                    >
                      {tile.label}
                    </div>
                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>
                      {tile.value}
                    </div>
                  </div>
                </DashboardCard>
              ))}
            </div>
          )}
        </TableBox>
      </TablesWrapper>
    </Wrapper>
  );
};

export default FraudDashboard;

/* Styled Components */
const Wrapper = styled.div`
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FilterWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: end;
  justify-content: left;
  background-color: white;
  width: 50%;
  padding: 14px;
  border-radius: 10px;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  background-color: white;
  padding: 20px;
  border-radius: 10px;
`;

const DashboardCard = styled.div<{ backgroundColor: string }>`
  background: ${(props) => props.backgroundColor};
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 10px;
  gap: 16px;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  &:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const CardIconWrap = styled.div`
  border-radius: 50%;
  padding: 10px;
`;

const CardLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #1e293b;
`;
const CardValue = styled.div`
  font-size: 20px;
  font-weight: bold;
`;

const ChartsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartBox = styled.div`
  background: white;
  padding: 16px;
  border-radius: 10px;
`;
const PieWrapper = styled.div`
  flex: 1;
  min-width: 250px;
  max-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const ChartTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #1e293b;
`;

const TablesWrapper = styled.div`
  display: grid;
  font-size: 14px;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TableBox = styled.div`
  background: white;
  padding: 16px;
  border-radius: 10px;
  width: 100%;
  overflow-x: auto;
`;
